/**
 * A barra de ações em lote da aba Lançamentos.
 *
 * Papel: reproduz `#bulk-bar` e `updateBulkBar` (l. 336–354, 2875–2900): a
 * contagem, a soma dos selecionados — pendentes inclusive — nos três jeitos da
 * origem, as cinco ações e o "✕ Cancelar".
 *
 * Como usar: a barra só existe com seleção; sem linha selecionada, ela não
 * desenha nada. A soma sai de `somaDaSelecao`, sobre as linhas guardadas na
 * seleção, à vista ou não. As ações chegam a quem monta por `onAcao`, que
 * pergunta antes de excluir, grava, limpa a seleção e avisa; a barra não
 * grava. Quem só tem `ver` vê a contagem, a soma e o "✕ Cancelar".
 *
 * O líquido negativo leva o menos antes do "R$" (exceção 11), e o "-R$" das
 * saídas e o "+R$" das entradas são o sinal que a origem escreve por conta
 * própria, com hífen.
 */
import { type AcaoDoLote, type LancamentoDaListagem, somaDaSelecao } from '@concierge/shared';
import { IconClock, IconSend } from '@tabler/icons-react';

import { cn } from '@/shared/lib/utils';
import { formatarReais } from '../formato';
import { Botao } from './kit/botao';

interface BarraDeLoteProps {
  /**
   * As linhas selecionadas, como a listagem atual as traz, ou como estavam
   * quando foram marcadas, se a tabela já não as mostra.
   */
  selecionados: readonly LancamentoDaListagem[];
  /** Falso para quem só tem `ver`. */
  podeEditar: boolean;
  /** Verdadeiro enquanto o lote não volta: as cinco ações ficam desabilitadas. */
  ocupado: boolean;
  /** Uma das cinco ações. */
  onAcao: (acao: AcaoDoLote) => void;
  /** O "✕ Cancelar", que só limpa a seleção. */
  onCancelar: () => void;
}

/** O filete vertical da barra. */
function Filete() {
  return <div aria-hidden="true" className="h-5 w-px bg-emb-lote-borda" />;
}

/**
 * A barra de lote.
 *
 * @param selecionados - As linhas selecionadas.
 * @param podeEditar - Mostra as cinco ações.
 * @param ocupado - Desabilita as cinco ações; o "✕ Cancelar" continua.
 * @param onAcao - Recebe a ação escolhida.
 * @param onCancelar - Limpa a seleção.
 * @returns A barra, ou nada sem seleção.
 */
export function BarraDeLote({
  selecionados,
  podeEditar,
  ocupado,
  onAcao,
  onCancelar,
}: BarraDeLoteProps) {
  const quantos = selecionados.length;
  if (quantos === 0) return null;

  const soma = somaDaSelecao(selecionados);
  const liquidoNegativo = Math.round(soma.liquido * 100) < 0;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-emb border border-emb-lote-borda bg-emb-lote-fundo px-4 py-2.5">
      <span className="text-[12px] font-bold text-emb-azul">
        {quantos} selecionado{quantos > 1 ? 's' : ''}
      </span>
      <Filete />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-texto-fraco">Soma:</span>
        {soma.exibicao === 'misto' && (
          <>
            <span className="text-[13px] font-bold text-sucesso">
              +{formatarReais(soma.entradas)}
            </span>
            <span className="text-[13px] font-bold text-perigo">
              -{formatarReais(soma.saidas)}
            </span>
            <span
              className={cn(
                'text-[13px] font-extrabold',
                liquidoNegativo ? 'text-perigo' : 'text-sucesso',
              )}
            >
              = {liquidoNegativo ? '' : '+'}
              {formatarReais(soma.liquido)}
            </span>
          </>
        )}
        {soma.exibicao === 'entradas' && (
          <span className="text-[13px] font-extrabold text-sucesso">
            +{formatarReais(soma.entradas)}
          </span>
        )}
        {soma.exibicao === 'saidas' && (
          <span className="text-[13px] font-extrabold text-perigo">
            -{formatarReais(soma.saidas)}
          </span>
        )}
      </div>
      <div className="flex-1" />
      {podeEditar && (
        <>
          <Botao
            variante="sucesso"
            tamanho="sm"
            disabled={ocupado}
            onClick={() => onAcao('marcar_pago')}
          >
            ✓ Marcar como Pago
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="sm"
            disabled={ocupado}
            onClick={() => onAcao('reverter_pendente')}
          >
            ↩ Reverter para Pendente
          </Botao>
          <Filete />
          <Botao
            variante="lancado"
            tamanho="sm"
            disabled={ocupado}
            onClick={() => onAcao('marcar_lancado')}
          >
            <IconSend size={13} aria-hidden="true" /> Marcar como Lançado
          </Botao>
          <Botao
            variante="provisionado"
            tamanho="sm"
            disabled={ocupado}
            onClick={() => onAcao('marcar_provisionado')}
          >
            <IconClock size={13} aria-hidden="true" /> Marcar como Provisionado
          </Botao>
          <Filete />
          <Botao
            variante="perigo"
            tamanho="sm"
            disabled={ocupado}
            onClick={() => onAcao('excluir')}
          >
            🗑 Excluir Selecionados
          </Botao>
        </>
      )}
      <Botao variante="fantasma" tamanho="sm" onClick={onCancelar}>
        ✕ Cancelar
      </Botao>
    </div>
  );
}
